import "@/global.css";
import {
  ClerkProvider,
  useAuth,
  useClerk,
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/expo";
import * as locales from "@clerk/localizations";
import { shadcn } from "@clerk/themes";
import * as Sentry from "@sentry/react-native";
import { WithSkiaWeb } from "@shopify/react-native-skia/lib/module/web";
import { version } from "canvaskit-wasm/package.json";
import { useFonts } from "expo-font";
import { Stack } from "expo-router";
import { DefaultTheme, ThemeProvider } from "expo-router/react-navigation";
import {
  ComponentProps,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Image, Platform } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { Center, GluestackUIProvider, Spinner, VStack } from "@/components/ui";
import {
  currentLocale,
  initialI18n as i18n,
  LocalizationProvider,
} from "@/hooks";

Sentry.init({
  dsn: "https://1e206aa6f4e1b5e6622869ada35314b8@o4510422327033858.ingest.us.sentry.io/4510422416031744",
  environment: process.env.EXPO_PUBLIC_ENV,
  enabled: !__DEV__,

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: true,

  // Enable Logs
  enableLogs: true,

  // Enable native
  enableNative: Platform.OS !== "web",

  // Configure Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  integrations: [
    Sentry.mobileReplayIntegration(),
    Sentry.feedbackIntegration(),
  ],

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

const AppLoading: FC = () => {
  return (
    <Center
      className="flex-1"
      style={{
        backgroundImage:
          "radial-gradient(ellipse 120% 60% at 0% 0%, hsl(258 90% 66% / 0.3), transparent 55%), linear-gradient(180deg, #0d0f1b 0%, #0d0f1b 100%)",
      }}
    >
      <VStack className="items-center gap-6 px-6">
        <Image
          resizeMode="contain"
          source={{ uri: "/logo-dark.png" }}
          style={{
            width: 195,
            height: 36,
            aspectRatio: 1475 / 272,
            maxWidth: "100%",
          }}
        />
        <Spinner color="rgb(var(--color-primary-400))" size="large" />
      </VStack>
    </Center>
  );
};

/**
 * Follows the `redirect_url` the page was loaded with (e.g. back to an OAuth
 * flow) once the user is signed in. Returns whether that redirect is pending,
 * so the app can keep the loading screen up instead of flashing the dashboard.
 */
const useSignInRedirect = (): boolean => {
  const clerk = useClerk();
  const { isSignedIn } = useAuth();
  // Read once on page load: the auth guard drops the query string as soon as
  // the user is signed in
  const [redirectUrl] = useState(() =>
    Platform.OS === "web"
      ? new URL(window.location.href).searchParams.get("redirect_url")
      : null
  );

  const target = useMemo(() => {
    if (!redirectUrl) return null;
    const url = new URL(redirectUrl, window.location.origin);
    const allowedOrigins = [
      window.location.origin,
      `https://${clerk.frontendApi}`,
    ];
    return allowedOrigins.includes(url.origin) ? url.href : null;
  }, [clerk.frontendApi, redirectUrl]);

  const redirecting = !!isSignedIn && target !== null;

  useEffect(() => {
    if (redirecting && target) window.location.replace(target);
  }, [redirecting, target]);

  return redirecting;
};

const AppContent: FC = () => {
  const [loaded] = useFonts({
    Inter: require("@expo-google-fonts/inter/400Regular/Inter_400Regular.ttf"),
    Inter_300Light: require("@expo-google-fonts/inter/300Light/Inter_300Light.ttf"),
    Inter_500Medium: require("@expo-google-fonts/inter/500Medium/Inter_500Medium.ttf"),
    Inter_600SemiBold: require("@expo-google-fonts/inter/600SemiBold/Inter_600SemiBold.ttf"),
    Inter_700Bold: require("@expo-google-fonts/inter/700Bold/Inter_700Bold.ttf"),
    Newsreader: require("@expo-google-fonts/newsreader/400Regular/Newsreader_400Regular.ttf"),
    Newsreader_300Light: require("@expo-google-fonts/newsreader/300Light/Newsreader_300Light.ttf"),
    Newsreader_500Medium: require("@expo-google-fonts/newsreader/500Medium/Newsreader_500Medium.ttf"),
    Newsreader_600SemiBold: require("@expo-google-fonts/newsreader/600SemiBold/Newsreader_600SemiBold.ttf"),
    Newsreader_700Bold: require("@expo-google-fonts/newsreader/700Bold/Newsreader_700Bold.ttf"),
  });

  const { isSignedIn, isLoaded } = useAuth();
  const redirecting = useSignInRedirect();

  return loaded && isLoaded && !redirecting ? (
    <Stack>
      <Stack.Protected guard={isSignedIn ?? false} redirectTo="/sign-in">
        <Stack.Screen name="(dashboard)" options={{ headerShown: false }} />
      </Stack.Protected>

      <Stack.Protected guard={!isSignedIn} redirectTo="/">
        <Stack.Screen name="(auth)" options={{ headerShown: false }} />
      </Stack.Protected>

      <Stack.Screen name="oauth-consent" options={{ headerShown: false }} />
      <Stack.Screen name="share" options={{ headerShown: false }} />
      <Stack.Screen name="+not-found" />
    </Stack>
  ) : (
    <AppLoading />
  );
};

const SentryUserSync: FC = () => {
  const { user, isLoaded } = useUser();

  useEffect(() => {
    if (!isLoaded) return;

    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        username: user.fullName ?? undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user, isLoaded]);

  return null;
};

const OrgActivation: FC = () => {
  const { isSignedIn } = useAuth();
  return isSignedIn ? <ActiveOrgSync /> : null;
};

const ActiveOrgSync: FC = () => {
  const { organization } = useOrganization();
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: true,
  });

  useEffect(() => {
    if (!isLoaded || organization || !setActive) return;
    const first = userMemberships?.data?.[0];
    if (!first) return;
    void setActive({ organization: first.organization.id });
  }, [isLoaded, organization, setActive, userMemberships?.data]);

  return null;
};

const clerkAppearance: ComponentProps<typeof ClerkProvider>["appearance"] = {
  theme: shadcn,
  elements: {
    card: {
      border: "1px solid rgb(var(--color-outline-100))",
      borderRadius: "16px 16px 0 0",
      boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
      overflow: "hidden",
    },
    dividerRow: {
      margin: "10px 0",
    },
    dividerText: {
      margin: "0 14px",
    },
    footer: "!border-t !border-gray-100 !rounded-none !p-0 !m-0 !gap-0",
    footerPages: "!rounded-none !shadow-none",
    formButtonPrimary:
      "!bg-[#0d0f1b] hover:!bg-[#111827] !text-white !font-semibold !text-[13px] !rounded-lg !h-[34px] transition-colors",
    form: "!gap-4",
    formButtonPrimaryLoader:
      "!text-white [&>*]:!stroke-white [&>*]:!fill-white",
    formButtonRow: "!mt-0",
    spinner: "!text-white [&>*]:!stroke-white",
    formFieldInput:
      "!border !border-gray-300 !rounded-lg !h-[40px] !pl-3 !pr-10 !text-[14px] focus:!border-gray-400 focus:!shadow-none focus:!outline-none",
    formFieldAction: "!underline",
    formFieldError: "!text-error-600 !text-xs !mt-2",
    formFieldLabel: "!text-typography-700 !text-[13px] !font-medium !mb-[5px]",
    formFieldRow: {
      marginBottom: "0px",
    },
    formFields: "!mb-0 !pb-0",
    header: {
      padding: "28px 20px 16px",
    },
    headerTitle: "!text-[30px] !font-normal !tracking-tight",
    backLink: "!underline",
    footerActionLink: "!underline",
    formFooterLink: "!underline",
    formHeaderSubtitleLink: "!underline",
    link: "!underline",
    headerSubtitle: "!text-typography-400 !text-[13px] !mt-1",
    main: {
      padding: "4px 20px 24px",
    },
    otpCodeFieldInput:
      "!border !border-gray-300 !rounded-lg !h-10 !w-10 !bg-white !text-gray-900 focus:!border-gray-400 focus:!shadow-none focus:!outline-none",
    alert:
      "!text-error-700 !bg-background-error !border !border-error-200 !rounded-lg !mt-3 !text-sm",
    alertText: "!text-error-700 !text-sm",
    alternativeMethodsBlockButton:
      "!border !border-gray-200 hover:!bg-gray-50 !rounded-lg !text-[13px] !font-medium !min-h-[38px] !h-auto !py-2 !px-3 !justify-start !whitespace-normal !text-left",
    alternativeMethodsBlockButtonText: "!text-typography-700",
    socialButtonsBlockButton:
      "!border !border-gray-200 hover:!bg-gray-50 !rounded-lg !text-[13px] !font-medium !min-h-[38px] !h-auto !py-2 !px-3 !justify-start",
    socialButtonsBlockButtonText: "!text-typography-700",
  },
  options: {
    logoPlacement: "none",
  },
  variables: {
    borderRadius: "0.5rem",
    colorDanger: "rgb(var(--color-error-600))",
    colorNeutral: "#6b7280",
    colorPrimary: "#0d0f1b",
    colorPrimaryForeground: "#ffffff",
    colorRing: "#9ca3af",
    fontFamily: "Inter, sans-serif",
  },
};

const Wrapper: FC<{ children?: ReactNode | ReactNode[] }> = ({ children }) => {
  const theme = {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      background: "hsl(0 0% 98%)",
      primary: "rgb(139, 92, 246)",
    },
  };
  const clerkKey = process.env.EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY;

  return (
    <ClerkProvider
      appearance={clerkAppearance}
      localization={{
        ...locales[
          currentLocale.languageTag.replace("-", "") as keyof typeof locales
        ],
        signIn: {
          start: {
            actionLink__join_waitlist: i18n.t("clerk.requestFreeTrial"),
            actionText__join_waitlist: i18n.t("clerk.noAccount"),
          },
          emailCode: {
            resendButton: i18n.t("clerk.resendCode"),
          },
          emailCodeMfa: {
            resendButton: i18n.t("clerk.resendCode"),
          },
          newDeviceVerificationNotice: i18n.t("clerk.newDeviceNotice"),
        },
        waitlist: {
          start: {
            actionLink: i18n.t("clerk.waitlistSignIn"),
            actionText: i18n.t("clerk.waitlistAlreadyAccess"),
            formButton: i18n.t("clerk.requestFreeTrial"),
            subtitle: i18n.t("clerk.waitlistSubtitle"),
            title: i18n.t("clerk.waitlistTitle"),
          },
          success: {
            message: i18n.t("clerk.waitlistSuccessMessage"),
            subtitle: i18n.t("clerk.waitlistSuccessSubtitle"),
            title: i18n.t("clerk.waitlistSuccessTitle"),
          },
        },
      }}
      publishableKey={clerkKey!}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      waitlistUrl="/sign-up"
    >
      <SentryUserSync />
      <OrgActivation />
      <LocalizationProvider>
        <ThemeProvider value={theme}>
          <GluestackUIProvider mode="light">
            <SafeAreaView className="flex-1">{children}</SafeAreaView>
          </GluestackUIProvider>
        </ThemeProvider>
      </LocalizationProvider>
    </ClerkProvider>
  );
};

const App: FC = () =>
  Platform.OS === "web" ? (
    <WithSkiaWeb
      opts={{
        locateFile: (file: string) =>
          `https://cdn.jsdelivr.net/npm/canvaskit-wasm@${version}/bin/full/${file}`,
      }}
      componentProps={{ children: <AppContent /> }}
      getComponent={() => Promise.resolve({ default: Wrapper })}
      fallback={
        <Wrapper>
          <AppLoading />
        </Wrapper>
      }
    />
  ) : (
    <Wrapper>
      <AppContent />
    </Wrapper>
  );

export { clerkAppearance, Wrapper };
export default Sentry.wrap(App);
